/**
 * Euchre score bar — collapsible. Trump info now lives in the
 * `EuchreTrumpPanel` (top-right) and per-hand trick progress lives in
 * the bottom-left widget (`EuchreHandProgress`), so this bar focuses
 * purely on the match race: team names + cumulative score.
 *
 * On phones the bar defaults to collapsed (matches Tractor/Swoop/
 * Regicide). Reuses the shared collapse pattern via
 * `.swoop-score-collapsed` + `.score-collapse-inline`.
 */

import { useState } from "react";
import type { EuchrePublicState } from "../../../../shared/games/euchre.js";
import { useWindowWidth } from "../../lib/uiHooks.js";

interface EuchreScoreBarProps {
  state: EuchrePublicState;
  yourSeat: number | null;
}

export const TEAM_NAMES = ["Green", "Red"] as const;
export const TEAM_COLORS = ["var(--my-team)", "var(--opp-team)"] as const;

export function teamColor(team: 0 | 1): string {
  return TEAM_COLORS[team];
}
export function teamName(team: 0 | 1): string {
  return TEAM_NAMES[team];
}

export function EuchreScoreBar({ state, yourSeat }: EuchreScoreBarProps) {
  const windowWidth = useWindowWidth();
  const [open, setOpen] = useState(() => windowWidth > 700);

  const myTeam = yourSeat !== null ? state.players[yourSeat]?.team ?? null : null;
  const team0Names = state.players.filter((p) => p.team === 0).map((p) => p.displayName).join(" + ");
  const team1Names = state.players.filter((p) => p.team === 1).map((p) => p.displayName).join(" + ");

  if (!open) {
    return (
      <button
        className="swoop-score-collapsed"
        onClick={() => setOpen(true)}
        title="Show scoreboard"
        aria-label="Show scoreboard"
      >
        {teamName(0)[0]} {state.teamScores[0]}–{state.teamScores[1]} {teamName(1)[0]} · Hand {state.roundNumber}
      </button>
    );
  }

  return (
    <div className="score-bar euchre-score-bar" role="img" aria-label="Euchre score">
      <div className="score-bar-header">
        <div className="score-bar-side defenders" style={{ color: teamColor(0) }}>
          <span className="role-label">
            {teamName(0).toUpperCase()}{myTeam === 0 ? " · you" : ""}
          </span>
          <span className="rank-label" title={team0Names}>{team0Names}</span>
        </div>
        <div className="score-bar-center">
          <span className="muted">Hand {state.roundNumber}</span>
        </div>
        <div className="score-bar-side attackers" style={{ color: teamColor(1) }}>
          <span className="role-label">
            {teamName(1).toUpperCase()}{myTeam === 1 ? " · you" : ""}
          </span>
          <span className="rank-label" title={team1Names}>{team1Names}</span>
        </div>
        <button
          className="score-collapse-inline"
          onClick={() => setOpen(false)}
          title="Hide scoreboard"
          aria-label="Hide scoreboard"
        >
          ×
        </button>
      </div>

      <div className="euchre-race-wrap">
        <RaceTrack team={0} score={state.teamScores[0]} target={state.matchTarget} />
        <RaceTrack team={1} score={state.teamScores[1]} target={state.matchTarget} />
      </div>
    </div>
  );
}

function RaceTrack({
  team,
  score,
  target,
}: {
  team: 0 | 1;
  score: number;
  target: number;
}) {
  const pct = Math.min(100, Math.max(0, (score / target) * 100));
  const color = teamColor(team);
  return (
    <div className="euchre-race-row">
      <span className="euchre-race-label" style={{ color }}>{teamName(team)[0]}</span>
      <div className="euchre-race-track">
        <div className="euchre-race-fill" style={{ width: `${pct}%`, background: color }} />
        {[2, 4, 6, 8].map((p) => (
          <div
            key={p}
            className="euchre-race-tick"
            style={{ left: `${(p / target) * 100}%` }}
          />
        ))}
        <div className="euchre-race-value">{score} / {target}</div>
      </div>
    </div>
  );
}
